import React, { useState } from "react";
import { Flex, Text, Box } from "@chakra-ui/react";
import { downloadUrl } from "utils";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import { trackWhitepaperDownloaded } from "components/event-tracking";
import { type EmbeddedWhitepaper } from "lib/sanity";
import { Section, type SectionProps } from "components/section";
import { HubSpotFormModal } from "components/hubspot";
import { Logo } from "components/brand/logo";
import { AuraCard, type AuraCardProps } from "ui/cards";
import { Link } from "ui/link";

type DownloadWhitepaperProps = {
  /**
   * Determines the color scheme of the aura card and the book cover
   * @default "green"
   */
  variant?: AuraCardProps["variant"];
} & EmbeddedWhitepaper &
  Omit<SectionProps, "variant" | "title">;

/**
 * Lead capture form for whitepaper download.
 * Uses `shortVariant` for the content. Typically a heading and description.
 *
 */
export function DownloadWhitepaper({
  title,
  fileName,
  fileUrl,
  posterImageUrl,
  hubSpotFormId,
  hubSpotCompanyField,
  shortVariant,
  variant = "green",
  ...sectionProps
}: DownloadWhitepaperProps) {
  const [success, setSuccess] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const displayedHubspotFields = hubSpotCompanyField ? ["firstname", "lastname", "company"] : ["firstname", "lastname"];
  const fileExtension: string = fileUrl.split(".").pop() ?? "";
  const fileDownloadUrl = `${fileUrl}?dl=${fileName}.${fileExtension}`;

  const onFormSubmit = (lead: Record<string, string>) => {
    trackWhitepaperDownloaded(fileName, lead, "Whitepaper");
    downloadUrl(fileDownloadUrl, `${fileName}.pdf`);
    setSuccess(true);
  };

  return (
    <Section {...sectionProps}>
      <AuraCard variant={variant} px={{ base: "big", md: "biggest", xl: "huge" }} py={{ base: "biggest", xl: "huge" }}>
        <Flex alignItems="center" gap={{ base: "biggest", lg: "huge" }} flexDir={{ base: "column", md: "row" }}>
          <Flex direction="column" maxW={{ base: "100%", md: "300px" }}>
            <BookCover isOpen={isHovered} title={title} colorScheme={variant} />
          </Flex>
          <Flex
            align="start"
            flexDir="column"
            w="100%"
            maxW={{ base: "mediaMaxW.mobile", md: "100%" }}
            mx={{ base: "auto", md: undefined }}
          >
            {shortVariant && (
              <ReactMarkdown
                components={{
                  h2: ({ children }) => (
                    <Text as="h2" textStyle={{ base: "headline2", md: "headline1", lg: "display3" }} mb="small" mt="0">
                      {children}
                    </Text>
                  ),
                  p: ({ children }) => (
                    <Text textStyle={{ base: "caption1", md: "body3" }} maxW={{ base: "100%", md: "52ch" }}>
                      {children}
                    </Text>
                  ),
                  strong: ({ children }) => (
                    <Text as="strong" fontWeight="bold">
                      {children}
                    </Text>
                  ),
                }}
              >
                {shortVariant}
              </ReactMarkdown>
            )}

            <Box
              pos="relative"
              mt="big"
              onMouseEnter={() => {
                setIsHovered(true);
              }}
              onMouseLeave={() => {
                setIsHovered(false);
              }}
            >
              <HubSpotFormModal
                hasLaunchButtonChevron
                launchButtonVariant="secondary"
                label="Read the whitepaper"
                displayedFields={displayedHubspotFields}
                formId={hubSpotFormId}
                modalHeading={title}
                modalLabel="Download whitepaper"
                successContent={
                  success && (
                    <Text textStyle="body3">
                      Thank you! If your download doesn&apos;t start immediately, please{" "}
                      <Link variant="inline" href={fileDownloadUrl}>
                        click here
                      </Link>
                      .
                    </Text>
                  )
                }
                onSubmit={onFormSubmit}
              />
            </Box>
          </Flex>
        </Flex>
      </AuraCard>
    </Section>
  );
}

/**
 *
 * 3D Book with hover animation.
 * @source inspired from https://vercel.com/resources
 */
function BookCover({
  isOpen,
  title,
  colorScheme,
}: {
  isOpen: boolean;
  title: string;
  colorScheme: DownloadWhitepaperProps["variant"];
}) {
  return (
    <Box width={{ base: "220px", md: "280px" }} height={{ base: "252px", md: "340px" }} userSelect="none">
      <Box
        boxShadow="md"
        width="var(--book-width)"
        height="var(--book-height)"
        pos="relative"
        sx={{
          perspective: "900px",
          "--book-width": "100%",
          "--book-height": "100%",
          "--book-depth": "54px",
          "--book-border-radius": "var(--chakra-radii-tinier)",
          "--book-cover-transform": "rotateY(-18deg) translateX(-1em) scale(1.05)",
        }}
      >
        {/* Book container, this will rotate outwards as if the book is opening on hover */}
        <Box
          boxSize="100%"
          borderRadius="var(--book-border-radius)"
          pos="relative"
          transition="transform 0.2s ease-out"
          transform={{ base: "var(--book-cover-transform)", md: isOpen ? "var(--book-cover-transform)" : "none" }}
          sx={{
            transformStyle: "preserve-3d",
          }}
        >
          {/* Book cover */}
          <Flex
            pos="absolute"
            inset="0"
            bgGradient={`linear(45deg, new.${colorScheme}.900, new.${colorScheme}.500 120%)`}
            borderRadius="var(--book-border-radius)"
            align="stretch"
            pl="bigger"
            pr="big"
            py="big"
          >
            <Flex flexDir="column" justify="space-between" gap="small">
              <Logo width="72px" fill={`new.${colorScheme}.50`} />
              <Text textStyle={{ base: "caption1", md: "body3" }} fontWeight="semibold" color="white">
                {title}
              </Text>
            </Flex>
          </Flex>
          {/* Spine */}
          <Box
            pos="absolute"
            left="0"
            top="0"
            bottom="0"
            width="smaller"
            bg={`new.${colorScheme}.900`}
            borderLeftRadius="var(--book-border-radius)"
            opacity={0.3}
            overflow="hidden"
            _before={{
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "6px",
              bgGradient: `linear(to-r, new.${colorScheme}.950 -20%, new.${colorScheme}.900 100%)`,
              zIndex: 1,
            }}
            _after={{
              content: '""',
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: "6px",
              bgGradient: `linear(to-l, new.${colorScheme}.950 -20%, new.${colorScheme}.900 100%)`,
              zIndex: 1,
            }}
          />

          {/* Inside pages */}
          <Box
            pos="absolute"
            right="0"
            top="4px"
            bottom="4px"
            width="48px"
            bgGradient="linear(90deg, brand.background.light 0%, brand.backgroundSecondary.light 100%)"
            transform="translateX(calc(var(--book-width) - var(--book-depth) / 2 - 0.5px)) rotateY(90deg) translateX(calc(var(--book-depth) / 2))"
          />
          {/* Back cover */}
          <Box
            pos="absolute"
            inset="0"
            bg={`new.${colorScheme}.950`}
            borderRadius="var(--book-border-radius)"
            transform="translateZ(calc(-1 * var(--book-depth)))"
          />
        </Box>
      </Box>
    </Box>
  );
}
